// Drag a personal task onto a list in the sidebar to move it there.
//
// Why a bridge instead of a Droppable: the task views (TaskListView /
// TaskBoardView) and the sidebar list tree each run their OWN
// @hello-pangea/dnd DragDropContext, and the library only resolves drops onto
// Droppables inside the same context — a row dragged in the content area can
// never produce a `destination` pointing at a sidebar row. Lifting one context
// over the whole app shell was rejected: the sidebar needs its own for project
// reordering, and nesting contexts isn't supported.
//
// So the sidebar rows publish their geometry through a `data-list-drop`
// attribute and this module hit-tests the pointer against those rects for the
// duration of a drag. The POINTER decides the target, not the dragged clone's
// centre (which is what hello-pangea itself uses): the pointer is what the user
// aims with, and a wide row's centre can still sit over the content area while
// the finger is already on a sidebar list.

import { signal } from "@preact/signals";
import { findList, updatePersonalTask } from "./personalTasks";
import { toastSuccess } from "../lib/toast";

/** A drop target: the id of the list the task should move into. */
export type ListDropTarget = number;

/** DOM attribute a sidebar row sets to `String(listId)` to become a target. */
export const LIST_DROP_ATTR = "data-list-drop";

/** True while a movable personal task is being dragged. Sidebar rows use it to
 *  show which rows are drop targets at all. */
export const listDropActiveSig = signal(false);

/** The target currently under the pointer, or null when none is. */
export const listDropHoverSig = signal<ListDropTarget | null>(null);

/** The dragged task's own list — never a target, a move onto it is a no-op. */
let excludedListId: number | null = null;

/** Minimal record shape the drop needs; `TaskRecord` satisfies it structurally. */
export interface ListDropRecord {
  id: number;
  source: "project" | "personal";
  parentId: number | null;
  listId: number | null;
}

/**
 * Can this record be moved between lists by dragging?
 *
 * Only personal tasks live in lists at all, and a step (a nested personal task,
 * `parentId !== null`) is pinned to its parent's list — the server rejects
 * moving one ("cannot move a step into a different list"), so it must not get
 * the affordance either.
 */
export function isListDropCandidate(rec: ListDropRecord): boolean {
  return rec.source === "personal" && rec.parentId === null;
}

function targetOf(el: Element): ListDropTarget | null {
  const raw = el.getAttribute(LIST_DROP_ATTR);
  if (raw === null) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function hitTest(x: number, y: number): ListDropTarget | null {
  // Rects are read per move rather than snapshotted at drag start: the sidebar
  // can scroll or a section can be expanded mid-drag. The row count is small
  // (a user's lists), so this stays cheap.
  for (const el of Array.from(document.querySelectorAll(`[${LIST_DROP_ATTR}]`))) {
    const id = targetOf(el);
    if (id === null || id === excludedListId) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue; // collapsed / hidden row
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
  }
  return null;
}

function track(x: number, y: number): void {
  const next = hitTest(x, y);
  if (listDropHoverSig.value !== next) listDropHoverSig.value = next;
}

function onPointerMove(e: PointerEvent): void {
  track(e.clientX, e.clientY);
}

function onTouchMove(e: TouchEvent): void {
  const t = e.touches[0];
  if (t) track(t.clientX, t.clientY);
}

/** True while the move listeners are attached (between drag start and release). */
let tracking = false;

function stopTracking(): void {
  if (!tracking) return;
  tracking = false;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("touchmove", onTouchMove);
  for (const type of RELEASE_EVENTS) window.removeEventListener(type, onRelease);
}

/**
 * Freeze the target at the moment the pointer is released.
 *
 * This is load-bearing, not a nicety: `onDragEnd` arrives only AFTER
 * hello-pangea's ~330ms drop animation, and by then the user has usually
 * already flicked the mouse off the row — the reflex right after letting go.
 * Tracking that far cleared the very target they had aimed at, so roughly every
 * other drop silently did nothing and the move had to be repeated. Reported
 * from production 2026-08-31 ("musste es zwei mal machen") and reproduced in
 * Chrome: release on the row then move away yielded no target.
 */
function onRelease(e: Event): void {
  // Sample the release position itself before freezing. Pointer moves are
  // coalesced, so a fast drag can have its last `pointermove` land just short
  // of the row while the release lands inside it — without this sample that
  // drop would find no target at all.
  // A cancelled gesture keeps whatever was last hovered but must not be
  // re-sampled — the cancel position is not a chosen target. (hello-pangea
  // reports such drags as "CANCEL" anyway, which the views never act on.)
  if (e.type.endsWith("cancel")) {
    stopTracking();
    return;
  }
  const p = e as Partial<PointerEvent> & { changedTouches?: TouchList };
  if (typeof p.clientX === "number" && typeof p.clientY === "number") {
    track(p.clientX, p.clientY);
  } else {
    const t = p.changedTouches?.[0];
    if (t) track(t.clientX, t.clientY);
  }
  stopTracking();
}

const RELEASE_EVENTS = ["pointerup", "pointercancel", "touchend", "touchcancel", "mouseup"] as const;

/**
 * Start tracking the pointer for the duration of a drag. Call from a
 * DragDropContext's `onDragStart`, only for an `isListDropCandidate` record.
 */
export function beginListDrop(excludeListId: number | null): void {
  excludedListId = excludeListId;
  listDropHoverSig.value = null;
  listDropActiveSig.value = true;
  tracking = true;
  window.addEventListener("pointermove", onPointerMove);
  // Touch drags: hello-pangea preventDefaults touchmove, after which pointer
  // events normally still fire — listening to both keeps tracking working
  // regardless of which stream the browser delivers.
  window.addEventListener("touchmove", onTouchMove, { passive: true });
  // Several release events for the same reason: whichever stream the browser
  // delivers, the target must be frozen on the first one that arrives.
  for (const type of RELEASE_EVENTS) window.addEventListener(type, onRelease);
}

/**
 * Return the target the pointer was over when it was released (null = none) and
 * clear the drag state. Safe to call from every `onDragEnd`, including drags
 * that never began tracking. See `onRelease` for why the value is frozen at
 * release rather than read here.
 */
export function endListDrop(): ListDropTarget | null {
  const hovered = listDropHoverSig.value;
  stopTracking();
  listDropActiveSig.value = false;
  listDropHoverSig.value = null;
  excludedListId = null;
  return hovered;
}

/**
 * Persist the move. Only `list_id` is sent: the server re-resolves the
 * (list-scoped) status to the target list's default/done status and clears
 * assignments when the task leaves a shared space. Errors already surface as a
 * toast via `api()` — e.g. 403 on a viewer-role list, 400 when moving another
 * user's task into a personal list.
 */
export async function moveTaskToList(taskId: number, target: ListDropTarget): Promise<void> {
  await updatePersonalTask(taskId, { list_id: target });
  toastSuccess(`Aufgabe nach „${findList(target)?.name ?? "Liste"}“ verschoben`);
}
